import { apiClient } from '../api/client'
import { API_ENDPOINTS } from '../api/endpoints'

// Types
export interface CustomFormField {
  id?: number
  customForm_ID?: number
  fieldName: string
  fieldLabel: string
  fieldType: 'text' | 'number' | 'date' | 'checkbox' | 'dropdown' | 'textarea' | 'email' | 'phone' | 'table'
  displayOrder: number
  isRequired: boolean
  isVisible: boolean
  placeholder?: string
  defaultValue?: string
  validationRules?: string
  options?: string // JSON string for dropdown options
  helpText?: string
  minLength?: number
  maxLength?: number
  minValue?: number
  maxValue?: number
  cssClass?: string
  columnWidth?: number
}

export interface CustomForm {
  id?: number
  name: string
  description?: string
  category?: string
  isActive: boolean
  showInMenu?: boolean
  menuIcon?: string
  menuOrder?: number
  createDate?: string
  createdBy_ID?: number
  fields: CustomFormField[]
  submissionCount?: number
}

export interface CustomFormSubmission {
  id?: number
  customForm_ID: number
  formName?: string
  submitDate: string
  submittedBy_ID?: number
  submittedBy_Name?: string
  submissionData: Record<string, any>
  status?: string
  notes?: string
  reviewedBy_ID?: number
  reviewDate?: string
}

export interface CreateCustomFormRequest {
  name: string
  description?: string
  category?: string
  showInMenu?: boolean
  menuIcon?: string
  menuOrder?: number
  fields: Omit<CustomFormField, 'id' | 'customForm_ID'>[]
}

export interface SubmitFormRequest {
  customForm_ID: number
  submissionData: Record<string, any>
  notes?: string
}

export interface FormReportRequest {
  customForm_ID: number
  fromDate?: string
  toDate?: string
  status?: string
  submittedBy_ID?: number
  searchTerm?: string
}

// Service Functions
export const customFormService = {
  // Get all forms
  getAll: async (): Promise<CustomForm[]> => {
    const response = await apiClient.get(API_ENDPOINTS.customForms.list)
    return response.data
  },

  // Get form by ID
  getById: async (id: number): Promise<CustomForm> => {
    const response = await apiClient.get(API_ENDPOINTS.customForms.get(id))
    return response.data
  },

  // Create new form
  create: async (form: CreateCustomFormRequest): Promise<CustomForm> => {
    const response = await apiClient.post(API_ENDPOINTS.customForms.create, form)
    return response.data
  },

  // Update form
  update: async (id: number, form: CreateCustomFormRequest): Promise<CustomForm> => {
    const response = await apiClient.put(API_ENDPOINTS.customForms.update(id), form)
    return response.data
  },

  // Delete form
  delete: async (id: number): Promise<void> => {
    await apiClient.delete(API_ENDPOINTS.customForms.delete(id))
  },

  // Submit form
  submit: async (id: number, submission: SubmitFormRequest): Promise<CustomFormSubmission> => {
    const response = await apiClient.post(API_ENDPOINTS.customForms.submit(id), submission)
    return response.data
  },

  // Get submissions
  getSubmissions: async (
    id: number,
    fromDate?: string,
    toDate?: string,
    status?: string,
    submittedById?: number
  ): Promise<CustomFormSubmission[]> => {
    const response = await apiClient.get(
      API_ENDPOINTS.customForms.submissions(id, fromDate, toDate, status, submittedById)
    )
    return response.data
  },

  // Get report
  getReport: async (request: FormReportRequest): Promise<CustomFormSubmission[]> => {
    const response = await apiClient.post(API_ENDPOINTS.customForms.report, request)
    return response.data
  },

  // Get forms for menu
  getMenuForms: async (): Promise<CustomForm[]> => {
    const response = await apiClient.get(API_ENDPOINTS.customForms.menu)
    const raw = response.data
    if (Array.isArray(raw)) return raw
    if (Array.isArray(raw?.data)) return raw.data
    if (Array.isArray(raw?.items)) return raw.items
    return []
  },

  // Get submission by ID
  getSubmission: async (formId: number, submissionId: number): Promise<CustomFormSubmission> => {
    const response = await apiClient.get(API_ENDPOINTS.customForms.getSubmission(formId, submissionId))
    return response.data
  },

  // Update submission
  updateSubmission: async (
    formId: number,
    submissionId: number,
    submission: SubmitFormRequest
  ): Promise<CustomFormSubmission> => {
    const response = await apiClient.put(
      API_ENDPOINTS.customForms.updateSubmission(formId, submissionId),
      submission
    )
    return response.data
  },

  // Delete submission
  deleteSubmission: async (formId: number, submissionId: number): Promise<void> => {
    await apiClient.delete(API_ENDPOINTS.customForms.deleteSubmission(formId, submissionId))
  },
}

